import { Link } from "react-router-dom";
import { Activity, Zap, Pill, Shield } from "lucide-react";
import { useAuth } from "../context/AuthContext";

export default function Landing() {
  const { user } = useAuth();
  const authed = user && user !== false;

  return (
    <div className="relative">
      {/* Hero */}
      <section className="relative min-h-[85vh] overflow-hidden">
        <div className="absolute inset-0">
          <img
            src="https://images.unsplash.com/photo-1682706841297-5524ba1faa9c?crop=entropy&cs=srgb&fm=jpg&w=2000&q=80"
            alt=""
            className="w-full h-full object-cover opacity-25"
          />
          <div className="absolute inset-0 bg-background/80" />
        </div>

        <div className="relative max-w-7xl mx-auto px-6 pt-20 pb-16 grid lg:grid-cols-12 gap-12 items-center">
          <div className="lg:col-span-7">
            <div className="inline-flex items-center gap-2 text-[11px] tracking-[0.35em] text-phosphor border border-phosphor/40 px-3 py-1 mb-8">
              <span className="w-1.5 h-1.5 bg-primary rounded-full animate-pulse" />
              FIBULA GAMES // CLINICAL TRAINER
            </div>
            <h1 className="text-5xl sm:text-6xl lg:text-7xl font-extrabold tracking-tight leading-[1.02]">
              Learn meds.<br/>
              Read rhythms.<br/>
              <span className="text-phosphor">In the dark.</span>
            </h1>
            <p className="mt-8 text-base sm:text-lg text-muted-foreground max-w-xl leading-relaxed">
              A game-style practice console for healthcare workers. Falling medication drills,
              live EKG strips with real monitor beeps, and spaced repetition that remembers what
              you keep missing. No account needed to play — sign in only when you want to save.
            </p>
            <div className="mt-10 flex flex-wrap gap-4">
              <Link
                to="/dashboard"
                data-testid="hero-start-btn"
                className="group inline-flex items-center gap-3 bg-primary text-primary-foreground px-7 py-4 text-sm font-semibold tracking-[0.2em] uppercase hover:bg-primary/90 transition-colors"
              >
                {authed ? "Continue Training" : "Play Now — No Login"}
                <Zap className="w-4 h-4 group-hover:translate-x-0.5 transition-transform" />
              </Link>
              {!authed && (
                <Link
                  to="/auth"
                  data-testid="hero-signin-btn"
                  className="inline-flex items-center gap-3 border border-phosphor/50 text-foreground px-7 py-4 text-sm font-semibold tracking-[0.2em] uppercase hover:bg-primary/10"
                >
                  Sign In to Save Progress
                </Link>
              )}
            </div>
          </div>

          <div className="lg:col-span-5">
            <div className="panel p-6 scanlines">
              <div className="flex items-center justify-between text-[10px] tracking-[0.3em] text-muted-foreground mb-4">
                <span>SYS // MONITOR</span>
                <span className="text-phosphor">● ONLINE</span>
              </div>
              <MiniPreview />
              <div className="grid grid-cols-3 gap-3 mt-5 text-center">
                {[
                  ["HR", "72"],
                  ["SpO₂", "99%"],
                  ["BP", "118/76"],
                ].map(([k, v]) => (
                  <div key={k} className="border border-phosphor/20 p-3">
                    <div className="text-[9px] tracking-widest text-muted-foreground">{k}</div>
                    <div className="text-phosphor text-xl font-bold">{v}</div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Features */}
      <section className="max-w-7xl mx-auto px-6 py-20 grid md:grid-cols-3 gap-6">
        {[
          {
            icon: Pill,
            title: "Medication Game",
            body:
              "Pick a class, set the pace. Descriptions fall from the top — type the generic or brand before they land. Spaced repetition hunts your weak spots.",
            testid: "feature-med",
          },
          {
            icon: Activity,
            title: "EKG Trainer",
            body:
              "Single-lead strips through complex 12-lead. Monomorphic VT? Mobitz II? Live scrolling canvas, monitor beeps, flatline alarms, full explanations.",
            testid: "feature-ekg",
          },
          {
            icon: Shield,
            title: "Not Clinical",
            body:
              "For practice and entertainment only. We are a drill ground — not a decision tool. Keep real patients in the hands of real teams.",
            testid: "feature-disclaimer",
          },
        ].map((f) => (
          <div key={f.title} data-testid={f.testid} className="panel p-7">
            <f.icon className="w-7 h-7 text-phosphor mb-5" />
            <h3 className="text-xl font-bold tracking-tight mb-3">{f.title}</h3>
            <p className="text-sm text-muted-foreground leading-relaxed">{f.body}</p>
          </div>
        ))}
      </section>
    </div>
  );
}

function MiniPreview() {
  return (
    <svg viewBox="0 0 400 120" className="w-full h-32">
      <defs>
        <linearGradient id="g" x1="0" x2="1">
          <stop offset="0" stopColor="hsl(142 71% 45%)" stopOpacity="0" />
          <stop offset="0.5" stopColor="hsl(142 71% 55%)" stopOpacity="1" />
          <stop offset="1" stopColor="hsl(142 71% 45%)" stopOpacity="0" />
        </linearGradient>
      </defs>
      <path
        d="M0 60 L60 60 L68 55 L75 60 L82 50 L88 20 L94 90 L100 60 L120 60 L135 56 L155 60 L240 60 L248 55 L255 60 L262 50 L268 20 L274 90 L280 60 L320 60 L400 60"
        fill="none"
        stroke="url(#g)"
        strokeWidth="2"
        style={{ filter: "drop-shadow(0 0 6px hsl(142 71% 45%))" }}
      />
    </svg>
  );
}
